export const apiGatewayEndpoints = {
  documents: {
    metadata: {
      url: '/v2/document/metadata',
    },
    missingMetadata: {
      url: '/v2/document/metadata/missing',
    },
  },
  quickPrompts: {
    getPromptCardsByCategory: {
      url: '/v2/prompt-card/models/latest',
    },
    getPromptCardsGroups: {
      url: '/v2/prompt-card-groups',
    },
    getPromptCardByVersionId: (id) => ({
      url: `/v2/prompt-card/versioned-content/${id}`, // v2 cutover (prompts +8 routes)
    }),
  },
  editorConfig: {
    getEmailTokensByOrg: {
      url: '/v2/editor-config/email-tokens',
    },
  },
  markdown: {
    downloadMessage: {
      url: '/markdown/download-message',
    },
  },
  authoredDocuments: {
    createOrUpdateAuthoredDocument: (id) => ({
      url: `/v2/authored-document/${id}`, // ARP: v2 cutover (authored-document)
      trackEvent: 'authoring_edit_committed',
    }),
    getAuthoredDocuments: {
      url: `/v2/authored-document`, // ARP: v2 cutover (authored-document)
    },
    getAuthoredDocumentById: (id) => ({
      url: `/v2/authored-document/${id}`, // ARP: v2 cutover (authored-document)
    }),
    downloadAuthoredDocument: (id) => ({
      url: `/v2/authored-document/download/${id}`, // ARP: v2 cutover (authored-document)
    }),
    duplicateAuthoredDocument: {
      url: `/v2/authored-document/duplicate`, // ARP: v2 cutover (authored-document)
    },
    authoredDocumentDownloadLink: (id, rid, filename) => ({
      url: `/v2/authored-document/download/${id}/${rid}/${filename}`, // ARP: v2 cutover (authored-document)
    }),
    deleteAuthoredDocumentById: (id) => ({
      url: `/v2/authored-document/${id}`, // ARP: v2 cutover (authored-document)
    }),
    uploadAuthoredImage: {
      url: '/images/authored-image-upload', // NOT migrated — no v2 route
    },
    uploadAuthoredOriginalImage: {
      url: '/images/authored-original-image-copy', // NOT migrated — no v2 route
    },
    getActivatorUrl: (id) => ({
      url: `/v2/activator/open/${id}`, // ARP: v2 cutover (activator-connector)
    }),
    publishToEwizard: (id) => ({
      url: `/v2/ewizard/publish/${id}`,
    }),
  },
  designSystems: {
    templates: {
      url: '/v2/design-system/templates?tactic=email',
    },
    getTemplateById: (id) => ({
      url: `/v2/design-system/templates/${id}`,
    }),
    getDesignSystemById: (id) => ({
      url: `/v2/design-system/${id}`,
    }),
    layouts: {
      url: '/v2/design-system/layouts?tactic=email',
    },
    layoutsThumbs: {
      url: '/thumbnail/layouts', // NOT migrated — thumbnail function
    },
    getLayoutById: (id) => ({
      url: `/v2/design-system/layout/?id=${id}`,
    }),
    getLayoutsByParentId: (parentId) => ({
      url: `/v2/design-system/layouts/?parentId=${parentId}&tactic=email`,
    }),
  },
  tacticContexts: {
    tacticContextTypes: (regionId) => ({
      url: `/v2/tactic-context-type/by-region/${regionId}`,
    }),
    regions: {
      url: '/v2/region',
    },
    tacticContexts: {
      url: '/tactics/tactic-contexts', // NOT migrated — different function/path
    },
    getTacticContextById: (id) => ({
      url: `/v2/tactic-context/${id}`,
    }),
  },
  collections: {
    reorderDocuments: {
      url: '/v2/collection/documents',
    },
    userCollections: {
      url: '/v2/collections',
    },
    addCollection: {
      url: '/v2/collection',
      trackEvent: 'collection_created',
    },
    updateCollection: {
      url: '/v2/collection',
      trackEvent: 'collection_updated',
    },
    removeCollection: {
      url: '/v2/collection',
      trackEvent: 'collection_deleted',
    },
    addDocumentToCollection: {
      url: '/v2/collection/add',
      trackEvent: 'document_added_to_collection',
    },
    removeDocumentFromCollection: {
      url: '/v2/collection/delete',
      trackEvent: 'document_removed_from_collection',
    },
    createCollectionSharedLink: {
      url: '/v2/collection/shared-link',
      trackEvent: 'collection_share_link_created',
    },
    validateSharedLink: (id) => ({
      url: `/v2/collection/shared-link/${id}`,
    }),
    createSharedCollection: {
      url: '/v2/collection/shared',
      trackEvent: 'shared_collection_added',
    },
  },
  // Intentionally on v1: glossaries are coupled to the translation flow (provider sync, Google + DeepL languages),
  // which is still on the Lambdas. Switch to /v2/arcane-glossary/* once translation is ported to Nest.
  glossary: {
    supportedLanguages: {
      url: '/arcane-glossary/supported-languages',
    },
    arcaneGlossaries: {
      url: '/arcane-glossary/glossaries',
    },
    createArcaneGlossary: {
      url: '/arcane-glossary/glossaries',
    },
    updateArcaneGlossary: (id) => ({
      url: `/arcane-glossary/glossaries/${id}`,
    }),
    availableGlossariesByTargetAndSource: (source, target) => ({
      url: `/arcane-glossary/available-glossaries?source=${source}&target=${target}`,
    }),
  },
  files: {
    userFiles: {
      url: 'my-files',
    },
    uploadFile: {
      url: 'file-upload',
    },
  },
  realtime: {
    requestToken: {
      url: '/v2/realtime/requestToken', // ARP: v2 cutover (realtime)
      ensureOrganization: true,
    },
  },
  s3Docs: {
    pdfUrl: {
      url: '/document/url',
    },
    pageCovers: {
      url: '/thumbnail/pages',
    },
    sourceUrl: {
      url: '/document/source-url',
    },
    thumbnail: {
      url: '/thumbnail',
    },
    thumbnailImportedImages: {
      url: '/thumbnail/images',
    },
    userGeneratedImages: {
      url: '/user/images',
    },
    authoredUserImages: {
      url: '/images/authored-user-images',
    },
    brandStylePreviews: {
      url: '/images/brand-styles-previews',
    },
  },
  user: {
    profile: {
      url: '/v2/user/profile', // v2 cutover (api-user)
    },
    userData: (userId) => ({
      url: `/v2/user/${userId}`, // v2 cutover (api-user)
    }),
    changePassword: {
      url: '/user/changePassword', // do-not-flip: not migrated to /v2
    },
  },
  logger: {
    putLog: {
      url: '/v2/logger/user-log', // ARP: v2 cutover (queue-logger)
    },
  },
  conversation: {
    userConversations: {
      url: '/conversations',
    },
    conversationById: (id) => ({
      url: `/conversations?conversationId=${id}`,
    }),
  },
  filters: {
    conversationFilters: {
      url: '/v2/filter/dropdowns',
    },
  },
  translation: {
    translateDocument: {
      url: '/translation/translate',
    },
    documentTranslations: {
      url: '/translation/document-translations',
    },
    getAvailableTranslations: {
      url: '/translation/translations',
    },
    getTargetLanguages: {
      url: '/translation/target-languages',
    },
    getSourceLanguages: {
      url: '/translation/source-languages',
    },
    sourceTranslationAvailable: {
      url: '/translation/source-translation-available',
    },
    translateSource: {
      url: '/translation/translate-source',
    },
  },
  platform: {
    platformNotifications: {
      url: '/v2/platform-notifications',
    },
  },
  mediaImages: {
    url: '/fetch_images',
  },
  userImages: {
    userImages: {
      url: '/v2/user-images',
    },
    brandStyles: {
      url: '/v2/image-brand-style',
    },
    getUserImageById: (id) => ({
      url: `/v2/user-images/${id}`,
    }),
    getBrandStyleById: (id) => ({
      url: `/v2/image-brand-style/${id}`,
    }),
    deleteUserImageById: (id) => ({
      url: `/v2/user-images/${id}`,
    }),
  },
};
